import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import Configurator from "@/components/Configurator";
import { Toaster } from "@/components/ui/sonner";
import { CONTACT } from "@/lib/data";
import { cityFromTitle, relatedLocations } from "@/lib/locations";

/**
 * LocationPage — one SEO landing page per city/area.
 * Keeps the old WebsiteBaker URL (/{slug}) alive with the old <title> and
 * meta description, so the per-city rankings survive the migration.
 */
export default function LocationPage({ location }) {
  const navigate = useNavigate();
  const bookingRef = useRef(null);
  const city = cityFromTitle(location.title);

  // Nav/footer links on a city page point back to the single-page home sections.
  const onNavigate = (id) => {
    if (id === "top") navigate({ pathname: "/" });
    else navigate({ pathname: "/", hash: id });
  };

  const scrollToBooking = () =>
    bookingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const canonicalUrl = `${window.location.origin}/${location.slug}`;

  return (
    <div className="min-h-screen bg-background font-sans text-foreground antialiased">
      <Seo
        title={location.title}
        siteName="Lej En Pølsevogn.dk"
        description={location.description}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Lej En Pølsevogn.dk",
          description: location.description,
          areaServed: city,
          telephone: CONTACT.phoneHref,
          email: CONTACT.email,
          url: canonicalUrl,
        }}
      />

      <NavBar onNavigate={onNavigate} />

      <main>
        <section className="px-5 pb-14 pt-32 text-center sm:pt-40">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Lej en pølsevogn i
          </p>
          <h1 className="mx-auto mt-3 max-w-3xl font-serif text-4xl font-bold tracking-tight text-ink sm:text-6xl">
            {city}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground">
            {location.description}
          </p>
          <button
            onClick={scrollToBooking}
            className="mt-8 rounded-full bg-poelse px-6 py-3 text-sm font-semibold text-paper transition-[background-color,transform] duration-200 hover:bg-poelse-deep active:scale-95"
          >
            Byg din forespørgsel
          </button>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            100% uforpligtende
          </p>
        </section>

        <section id="booking" ref={bookingRef} className="scroll-mt-24">
          <Configurator />
        </section>

        <section className="mx-auto max-w-6xl px-5 py-16">
          <h2 className="font-serif text-2xl font-bold tracking-tight text-ink">
            Vi kører også til
          </h2>
          <div className="mt-6 flex flex-wrap gap-3">
            {relatedLocations(location.slug).map((loc) => (
              <Link
                key={loc.slug}
                to={`/${loc.slug}`}
                className="rounded-full border border-border px-4 py-2 text-sm text-muted-foreground transition-colors duration-200 hover:border-poelse hover:text-ink"
              >
                {cityFromTitle(loc.title)}
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer onNavigate={onNavigate} />
      <Toaster position="bottom-center" richColors />
    </div>
  );
}
